export const IDENTIFIER_MAX_LENGTH = 15;
export const POSSIBLE_EID_MIN_LENGTH = 20;
export const BLOCKED_ZERO_IMEI = '000000000000000';
export const ALLOWED_EXCEPTION_IMEI = '100000000000001';

export const IDENTIFIER_REASONS = {
  EMPTY: 'empty',
  INCOMPLETE: 'incomplete',
  NON_NUMERIC: 'nonNumeric',
  INVALID_LUHN: 'invalidLuhn',
  SERIAL_WITHOUT_LATIN_LETTER: 'serialWithoutLatinLetter',
  TOO_LONG: 'tooLong',
  POSSIBLE_EID: 'possibleEid',
  VALID_IMEI: 'validImei',
  VALID_SERIAL: 'validSerial',
  NOT_REQUIRED: 'notRequired',
};

const result = (valid, kind, reason, message, state) => ({
  valid,
  kind,
  reason,
  message,
  state,
});

export const isValidLuhn = (value) => {
  let sum = 0;
  let shouldDouble = false;

  for (let index = value.length - 1; index >= 0; index -= 1) {
    let digit = Number(value[index]);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const createTooLongIdentifierStatus = ({
  allowSerial = false,
  label = 'IMEI',
  length,
} = {}) => result(
  false,
  allowSerial ? null : 'imei',
  IDENTIFIER_REASONS.TOO_LONG,
  allowSerial
    ? 'IMEI или серийный номер должен содержать не более 15 символов'
    : `${label} должен содержать 15 цифр${length ? `. Введено: ${length}` : ''}`,
  'error',
);

export const createPossibleEidStatus = () => result(
  false,
  null,
  IDENTIFIER_REASONS.POSSIBLE_EID,
  'Считанный код длиннее IMEI. Возможно, был считан EID',
  'error',
);

export const createNonNumericIdentifierStatus = (label = 'IMEI') => result(
  false,
  'imei',
  IDENTIFIER_REASONS.NON_NUMERIC,
  `${label} должен содержать только цифры`,
  'error',
);

export const createMissingSecondImeiStatus = () => result(
  true,
  null,
  IDENTIFIER_REASONS.NOT_REQUIRED,
  'IMEI2 отсутствует',
  'neutral',
);

export const createOptionalIdentifierStatus = () => result(
  true,
  null,
  IDENTIFIER_REASONS.NOT_REQUIRED,
  'IMEI или серийный номер не указан. Можно продолжить без него',
  'neutral',
);

export const validateDeviceIdentifier = (rawValue, {
  allowSerial = false,
  label = 'IMEI',
} = {}) => {
  const value = String(rawValue || '').trim();

  if (!value) {
    return result(
      false,
      null,
      IDENTIFIER_REASONS.EMPTY,
      allowSerial ? 'Введите IMEI или серийный номер' : `Введите 15 цифр ${label}`,
      'neutral',
    );
  }

  if (value.length > IDENTIFIER_MAX_LENGTH) {
    return createTooLongIdentifierStatus({ allowSerial, label, length: value.length });
  }

  if (allowSerial && value.length < IDENTIFIER_MAX_LENGTH) {
    if (!/[A-Za-z]/.test(value)) {
      return result(
        false,
        'serial',
        IDENTIFIER_REASONS.SERIAL_WITHOUT_LATIN_LETTER,
        'Серийный номер должен содержать хотя бы одну латинскую букву',
        'error',
      );
    }

    return result(
      true,
      'serial',
      IDENTIFIER_REASONS.VALID_SERIAL,
      'Серийный номер принят',
      'success',
    );
  }

  if (value.length < IDENTIFIER_MAX_LENGTH) {
    return result(
      false,
      'imei',
      IDENTIFIER_REASONS.INCOMPLETE,
      `${label} должен содержать 15 цифр. Введено: ${value.length}`,
      'error',
    );
  }

  if (!/^\d{15}$/.test(value)) {
    return createNonNumericIdentifierStatus(label);
  }

  if (
    value === ALLOWED_EXCEPTION_IMEI
    || (value !== BLOCKED_ZERO_IMEI && isValidLuhn(value))
  ) {
    return result(
      true,
      'imei',
      IDENTIFIER_REASONS.VALID_IMEI,
      `${label} введён корректно`,
      'success',
    );
  }

  return result(
    false,
    'imei',
    IDENTIFIER_REASONS.INVALID_LUHN,
    `${label} не прошёл проверку. Проверьте правильность цифр`,
    'error',
  );
};

export const getProjectedIdentifierValue = (target, insertedValue) => {
  const value = String(target?.value || '');
  const selectionStart = Number.isInteger(target?.selectionStart) ? target.selectionStart : value.length;
  const selectionEnd = Number.isInteger(target?.selectionEnd) ? target.selectionEnd : selectionStart;

  return `${value.slice(0, selectionStart)}${insertedValue || ''}${value.slice(selectionEnd)}`;
};

export const trackIdentifierOverflowAttempt = (previousAttempt, currentValue, projectedValue) => {
  const baseValue = String(currentValue || '');
  const projected = String(projectedValue || '');
  const sameBaseValue = previousAttempt?.baseValue === baseValue;
  const attemptedAdditionLength = Math.max(projected.length - baseValue.length, 1);

  return {
    baseValue,
    attemptedLength: sameBaseValue
      ? previousAttempt.attemptedLength + attemptedAdditionLength
      : projected.length,
    digitsOnly: (sameBaseValue ? previousAttempt.digitsOnly : true) && /^\d+$/.test(projected),
  };
};

export const isPossibleEidAttempt = (attempt) => (
  attempt?.attemptedLength >= POSSIBLE_EID_MIN_LENGTH
  && attempt.digitsOnly
);

export const getInvalidIdentifierPopupMessage = (status, {
  allowSerial = false,
  label = 'IMEI',
} = {}) => {
  if (status.reason === IDENTIFIER_REASONS.EMPTY && allowSerial) {
    return 'Введите IMEI или серийный номер и попробуйте ещё раз';
  }

  if (status.reason === IDENTIFIER_REASONS.EMPTY) {
    return `Введите ${label} и попробуйте ещё раз`;
  }

  if (status.reason === IDENTIFIER_REASONS.SERIAL_WITHOUT_LATIN_LETTER) {
    return 'Серийный номер введён неправильно. Он должен содержать хотя бы одну латинскую букву';
  }

  return `${label} введён неправильно. Проверьте ${label} и попробуйте ещё раз`;
};
